import {
  type ResumeState,
  type AIRewriteRequest,
  type AIRewriteResponse,
  type AIErrorResponse,
} from '@/types';

const STORAGE_KEY = 'ai-free-resume-state';
const ANON_ID_KEY = 'afr-anon-id';

export function getAnonymousId(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  let id = localStorage.getItem(ANON_ID_KEY);
  if (!id) {
    id = `anon_${crypto.randomUUID()}`;
    localStorage.setItem(ANON_ID_KEY, id);
  }
  return id;
}

export function formatDateRange(start?: string, end?: string): string {
  if (!start && !end) return '';
  const s = start ? formatMonth(start) : '';
  const e = end ? (end.toLowerCase() === 'present' ? 'Present' : formatMonth(end)) : '';
  if (s && e) return `${s} – ${e}`;
  return s || e;
}

function formatMonth(value: string): string {
  const [year, month] = value.split('-');
  if (!year || !month) return value;
  const date = new Date(`${year}-${month}-01`);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
}

export function defaultState(): ResumeState {
  return {
    profile: {
      fullName: '',
      email: '',
      phone: '',
      location: '',
      summary: '',
    },
    experience: [
      {
        id: 'exp-1',
        company: '',
        title: '',
        startDate: '',
        endDate: '',
        bullets: [''],
      },
    ],
    education: [
      {
        id: 'edu-1',
        school: '',
        degree: '',
        field: '',
        graduationDate: '',
      },
    ],
    skills: [],
    selectedTemplate: 'classic',
    aiUsage: {
      anonymousId: getAnonymousId(),
      usedCount: 0,
      maxFreeCount: 2,
      subscription: 'free',
    },
  };
}

export function loadState(): ResumeState {
  if (typeof window === 'undefined') {
    return defaultState();
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ResumeState;
      const anonId = parsed.aiUsage?.anonymousId || getAnonymousId();
      return {
        ...defaultState(),
        ...parsed,
        aiUsage: {
          ...defaultState().aiUsage,
          ...parsed.aiUsage,
          anonymousId: anonId,
        },
      };
    }
  } catch {
    // ignore
  }
  return defaultState();
}

export function saveState(state: ResumeState): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function getQuotaLabel(used: number, max: number, subscription: ResumeState['aiUsage']['subscription'] = 'free'): string {
  const remaining = Math.max(0, max - used);
  if (subscription === 'free') {
    return `${remaining} of ${max} remaining`;
  }
  return `${remaining} remaining today`;
}

export function getAiUsageDescription(used: number, max: number, subscription: ResumeState['aiUsage']['subscription'] = 'free'): string {
  const remaining = Math.max(0, max - used);
  if (subscription === 'free') {
    return `Free AI rewrites: ${remaining} of ${max} per browser ID. Upgrade to Pro for 50 per day.`;
  }
  return `Pro AI rewrites: ${remaining} remaining today. Resets at midnight UTC.`;
}

export function isAiApiConfigured(): boolean {
  const mock = String(import.meta.env.VITE_AI_API_MOCK) === 'true';
  const rawBaseUrl = import.meta.env.VITE_API_BASE_URL;
  const hasBaseUrl =
    typeof rawBaseUrl === 'string' &&
    rawBaseUrl !== 'undefined' &&
    rawBaseUrl.length > 0;
  return mock || hasBaseUrl;
}

export async function rewriteBullets(
  request: AIRewriteRequest,
  usedCount?: number,
  maxFreeCount?: number
): Promise<AIRewriteResponse> {
  const USE_MOCK = String(import.meta.env.VITE_AI_API_MOCK) === 'true';

  if (USE_MOCK) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    const effectiveQuota = usedCount !== undefined && maxFreeCount !== undefined ? maxFreeCount - usedCount : 1;
    if (effectiveQuota <= 0) {
      const err: AIErrorResponse = {
        error: 'RATE_LIMITED',
        message: "You've used your free AI rewrites. Upgrade to Pro for more.",
      };
      throw err;
    }
    if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).__aiMockFail === true) {
      const err: AIErrorResponse = { error: 'AI_PROVIDER_ERROR', message: 'AI provider failed. Please try again.' };
      throw err;
    }
    const original = request.resumeText.trim();
    return {
      bullets: [
        `${original} — improved with measurable impact and action-oriented language.`,
        `Led cross-functional initiatives that increased team output by 20%.`,
      ],
      quotaRemaining: Math.max(0, effectiveQuota - 1),
      model: 'mock',
    };
  }

  const baseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '';
  const response = await fetch(`${baseUrl}/api/ai/rewrite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as AIErrorResponse;
    throw error;
  }

  return (await response.json()) as AIRewriteResponse;
}

export function cn(...classes: (string | false | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

export default cn;
